import { useStoreState } from '@/stores/store';
import { useCallback, useLayoutEffect, useState } from 'react';
import { View } from 'react-native';
import api from '../../api/axios';
import { useWordsetContext } from '@/hooks/useWordsetContext';
import { useNoInternetDialog } from '@/hooks/useNoInternetDialog';
import { SecondHeader } from './SecondHeader';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { styles as GlobalStyles } from './styles';
import { Icon } from '@/components/Icon';
import { logError } from '@/utils/logHandling';
import { SetSaveModal } from '@/components/SetsSelectedData/SetSaveModal';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { Text } from '@/components/Text';
import { fetchUpdatedPatientWithRetry } from '@/utils/fetchUpdatedPatientWithRetry';
import { fetchPatientsList } from '@/api/services/user';
import { replacedPatientProfileHeader } from '@/utils/stringHelpers';

export function Header({ props }) {
  const {
    apiKeyName,
    setId,
    editData,
    patientId,
    dynamicName,
    selectedWords,
    dataKeyName,
  } = useWordsetContext();
  const { checkInternet } = useNoInternetDialog();

  const noWordsSelected = !selectedWords.length;
  const [isDemoTherapistVisible, setIsDemoTherapistVisible] = useState(false);
  const { isTherapistDemo } = useStoreState((store) => store);

  const [isVisible, setIsVisible] = useState(false);
  const [inputState, setInputState] = useState({
    name: '',
    appDisplayName: '',
  });

  const handleButtonPress = useCallback(() => {
    if (isTherapistDemo) {
      setIsDemoTherapistVisible(true);
    } else {
      setIsVisible((prevState) => {
        if (setId && editData != null) {
          setInputState({
            name: editData.name,
            appDisplayName: editData.appDisplayName,
          });
        }
        return !prevState;
      });
    }
  }, [setId, editData]);

  const handleInputChange = useCallback((key) => {
    return (value) => {
      setInputState((prevState) => {
        return {
          ...prevState,
          [key]: value,
        };
      });
    };
  }, []);

  const handleModalAcceptButton = async () => {
    try {
      await checkInternet();

      if (setId) {
        const state = { ...inputState };
        if (state.name == '') {
          state.name = 'Neues Wörterset';
        }
        if (state.appDisplayName == '') {
          state.appDisplayName = 'Wörter';
        }

        const bodyData = {
          ...editData,
          [`${dynamicName}Items`]: [],
          ...state,
          updatedAt: new Date().toISOString(),
          patientId: patientId,
          [dataKeyName]: selectedWords,
        };
        await api.put(
          `/api/patients/${patientId}/${apiKeyName}/${setId}?api_version=v2`,
          bodyData,
        );

        const { patients } = await fetchUpdatedPatientWithRetry(
          patientId,
          'wordsets',
          setId,
        );

        props.navigation.navigate('PatientProfile', {
          id: patientId,
          patients,
          replacedTitle: replacedPatientProfileHeader({
            patients,
            patientId,
            patient: undefined,
          }),
        });
      } else {
        const state = { ...inputState };
        if (state.name == '') {
          state.name = 'Neues Wörterset';
        }
        if (state.appDisplayName == '') {
          state.appDisplayName = 'Wörter';
        }

        const bodyData = {
          ...state,
          patientId: patientId,
          [dataKeyName]: selectedWords,
        };

        const { data } = await api.post(
          `/api/patients/${patientId}/${apiKeyName}?api_version=v2`,
          bodyData,
        );

        const { patients } = await fetchUpdatedPatientWithRetry(
          patientId,
          'wordsets',
          data._id,
        );
        props.navigation.navigate('PatientProfile', {
          id: patientId,
          patients,
          replacedTitle: replacedPatientProfileHeader({
            patients,
            patientId,
            patient: undefined,
          }),
        });
      }
    } catch (error) {
      logError('Error creating set', error);
    }
  };

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerRight: () => (
        <View style={{ gap: 30, flexDirection: 'row' }}>
          <AppHeaderAction
            primary
            onPress={() => props.navigation.goBack()}
            icon={<Icon name="close" scale={1.2} />}
            text="Abbrechen"
          />
          <AppHeaderAction
            primary
            disabled={noWordsSelected}
            onPress={handleButtonPress}
            icon={<Icon name="save" />}
            text="Speichern"
          />
        </View>
      ),
    });
  }, [noWordsSelected, handleButtonPress]);

  return (
    <View style={GlobalStyles.header}>
      <Modal
        isVisible={isDemoTherapistVisible}
        title="In der Demo nicht möglich"
        confirmActionButton={
          <Button
            label="OK"
            look="primary"
            onPress={() => setIsDemoTherapistVisible(false)}
          />
        }
        onClose={() => setIsDemoTherapistVisible(false)}
      >
        <Text style={{ fontSize: 18 }}>
          Registrieren Sie sich bei neolexon, um Ihre eigenen Patient:innen und
          Übungssets zu erstellen.
        </Text>
      </Modal>

      <SecondHeader />

      <SetSaveModal
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        handleInputChange={handleInputChange}
        handleModalAcceptButton={handleModalAcceptButton}
        inputState={inputState}
        handleButtonPress={handleButtonPress}
        modalTitle="Wörterset speichern"
        apiKeyName={apiKeyName}
      />
    </View>
  );
}
